import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Permission } from '@cms/shared';

/**
 * §6.3: permission strings, never role names. Every guard in the SPA asks "may this
 * actor do X", so adding a role never means hunting for `role === 'admin'` checks.
 *
 * This is a *convenience* layer only. The API is the authority — the SPA hiding a button
 * is UX, not access control, and every route it guards is also enforced server-side.
 */
interface PermissionContextValue {
  permissions: ReadonlySet<Permission>;
}

const PermissionContext = createContext<PermissionContextValue>({ permissions: new Set() });

export function PermissionProvider({
  permissions,
  children,
}: {
  permissions: readonly Permission[];
  children: ReactNode;
}) {
  const value = useMemo(() => ({ permissions: new Set(permissions) }), [permissions]);
  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function useHasPermission(): (permission: Permission) => boolean {
  const { permissions } = useContext(PermissionContext);
  return (permission) => permissions.has(permission);
}

export function Can({ permission, children }: { permission: Permission; children: ReactNode }) {
  return useHasPermission()(permission) ? <>{children}</> : null;
}

/** The raw permission set, for callers that filter rather than ask about one permission. */
export function useContextPermissions(): ReadonlySet<Permission> {
  return useContext(PermissionContext).permissions;
}
